import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';

interface AuthCtx {
  user: User | null;
  loading: boolean;
  logIn:     (email: string, password: string) => Promise<void>;
  signUp:    (email: string, password: string) => Promise<void>;
  logOut:    () => Promise<void>;
  reset:     (email: string) => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  const value = useMemo<AuthCtx>(() => ({
    user,
    loading,
    logIn:  async (email, pw) => { await signInWithEmailAndPassword(auth, email, pw); },
    signUp: async (email, pw) => { await createUserWithEmailAndPassword(auth, email, pw); },
    logOut: async ()          => { await signOut(auth); },
    reset:  async (email)     => { await sendPasswordResetEmail(auth, email); },
  }), [user, loading]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
